import React, { useState } from 'react'
import { X, Save } from 'lucide-react'
import { projectsApi, CbsAssessmentRow, VendorResponseRow } from '@/lib/projectsApi'
import { useToast } from '@/contexts/ToastContext'

const CBS_QUESTIONS = [
  { code: 'q1', num: 1, text: 'Digital display or input device?' },
  { code: 'q2', num: 2, text: 'Programmable electronic device (CPU/MCU/PLC)?' },
  { code: 'q3', num: 3, text: 'Connection port for maintenance or external interface?' },
  { code: 'q4', num: 4, text: 'Firmware/software versions or configurable parameters?' },
  { code: 'q5', num: 5, text: 'Digital data processing or protocol communication?' },
  { code: 'q6', num: 6, text: 'Diagnostic information or event history?' },
]

interface Profile {
  id: string
  vendor_name: string
  model_name: string
  system_name: string
}

interface Props {
  profile: Profile
  cbs: CbsAssessmentRow
  vendorResponses: VendorResponseRow[]
  onClose: () => void
  onSaved: () => void
}

export const FullEditAssessmentModal: React.FC<Props> = ({ profile, cbs, vendorResponses, onClose, onSaved }) => {
  const { error: showError, success } = useToast()
  const [saving, setSaving] = useState(false)

  const [prof, setProf] = useState({ ...profile })
  const [cbsState, setCbsState] = useState({ ...cbs })
  const [vrState, setVrState] = useState(vendorResponses.map(v => ({ ...v })))

  const setCbsField = (key: string, value: any) => setCbsState(prev => ({ ...prev, [key]: value }))
  const setVrField = (id: string, key: string, value: any) =>
    setVrState(prev => prev.map(v => v.id === id ? { ...v, [key]: value } : v))

  const handleSave = async () => {
    setSaving(true)
    try {
      if (prof.vendor_name !== profile.vendor_name || prof.model_name !== profile.model_name || prof.system_name !== profile.system_name) {
        await projectsApi.updateProfile(profile.id, {
          vendor_name: prof.vendor_name, model_name: prof.model_name, system_name: prof.system_name,
        })
      }
      await projectsApi.updateCbs(cbs.id, {
        q1_answer: cbsState.q1_answer as any, q2_answer: cbsState.q2_answer as any,
        q3_answer: cbsState.q3_answer as any, q4_answer: cbsState.q4_answer as any,
        q5_answer: cbsState.q5_answer as any, q6_answer: cbsState.q6_answer as any,
        q1_evidence_note: cbsState.q1_evidence_note, q2_evidence_note: cbsState.q2_evidence_note,
        q3_evidence_note: cbsState.q3_evidence_note, q4_evidence_note: cbsState.q4_evidence_note,
        q5_evidence_note: cbsState.q5_evidence_note, q6_evidence_note: cbsState.q6_evidence_note,
      })
      for (const v of vrState) {
        await projectsApi.updateVendorResponse(v.id, {
          dropdown_response: v.dropdown_response,
          detailed_response: v.detailed_response,
          remark: v.remark,
        })
      }
      success('Assessment updated')
      onSaved()
    } catch (err: any) {
      showError(err.message ?? 'Save failed')
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white dark:bg-slate-800 rounded-lg shadow-xl max-w-3xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        <div className="flex items-center justify-between px-5 py-3 border-b border-slate-200 dark:border-slate-700">
          <h2 className="text-base font-semibold">Edit Risk Assessment</h2>
          <button onClick={onClose} className="p-1 hover:bg-slate-100 dark:hover:bg-slate-700 rounded">
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-5 space-y-5">

          {/* Profile */}
          <section>
            <h3 className="text-xs font-semibold text-slate-400 uppercase mb-2">System Profile</h3>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
              <label className="block">
                <span className="text-[10px] text-slate-500">Vendor</span>
                <input value={prof.vendor_name} onChange={e => setProf(p => ({ ...p, vendor_name: e.target.value }))}
                       className="w-full px-2 py-1 text-xs border border-slate-200 dark:border-slate-700 rounded bg-white dark:bg-slate-900 text-slate-900 dark:text-white" />
              </label>
              <label className="block">
                <span className="text-[10px] text-slate-500">Model</span>
                <input value={prof.model_name ?? ''} onChange={e => setProf(p => ({ ...p, model_name: e.target.value }))}
                       className="w-full px-2 py-1 text-xs border border-slate-200 dark:border-slate-700 rounded bg-white dark:bg-slate-900 text-slate-900 dark:text-white" />
              </label>
              <label className="block">
                <span className="text-[10px] text-slate-500">System name</span>
                <input value={prof.system_name ?? ''} onChange={e => setProf(p => ({ ...p, system_name: e.target.value }))}
                       className="w-full px-2 py-1 text-xs border border-slate-200 dark:border-slate-700 rounded bg-white dark:bg-slate-900 text-slate-900 dark:text-white" />
              </label>
            </div>
          </section>

          {/* CBS */}
          <section>
            <h3 className="text-xs font-semibold text-slate-400 uppercase mb-2">CBS Initial Assessment</h3>
            <div className="space-y-2">
              {CBS_QUESTIONS.map(q => (
                <div key={q.code} className="border border-slate-200 dark:border-slate-700 rounded p-2">
                  <div className="flex items-start gap-2 mb-1.5">
                    <span className="text-[10px] font-semibold text-slate-500 mt-0.5">Q{q.num}</span>
                    <span className="text-xs flex-1">{q.text}</span>
                    <select
                      value={(cbsState[`${q.code}_answer` as keyof typeof cbsState] as string) ?? ''}
                      onChange={e => setCbsField(`${q.code}_answer`, e.target.value === '' ? null : e.target.value)}
                      className="text-[10px] border border-slate-200 dark:border-slate-700 rounded px-1.5 py-0.5 bg-white dark:bg-slate-900 text-slate-900 dark:text-white"
                    >
                      <option value="">--</option>
                      <option value="Y">YES</option>
                      <option value="N">NO</option>
                    </select>
                  </div>
                  <input
                    placeholder="Evidence note..."
                    value={(cbsState[`${q.code}_evidence_note` as keyof typeof cbsState] as string) ?? ''}
                    onChange={e => setCbsField(`${q.code}_evidence_note`, e.target.value || null)}
                    className="w-full px-2 py-1 text-[11px] border border-slate-200 dark:border-slate-700 rounded bg-white dark:bg-slate-900 text-slate-900 dark:text-white"
                  />
                </div>
              ))}
            </div>
          </section>

          {/* Vendor responses */}
          {vrState.length > 0 && (
            <section>
              <h3 className="text-xs font-semibold text-slate-400 uppercase mb-2">Vendor Responses</h3>
              <div className="space-y-3">
                {vrState.map(v => (
                  <div key={v.id} className="border border-slate-200 dark:border-slate-700 rounded p-2">
                    <div className="flex items-baseline gap-2 mb-1.5">
                      <span className="text-[10px] font-mono bg-slate-100 dark:bg-slate-700 px-1.5 py-0.5 rounded font-semibold">{v.question_code}</span>
                      <span className="text-[10px] text-slate-400 uppercase">{v.category}</span>
                    </div>
                    <div className="text-xs mb-2 text-slate-600 dark:text-slate-300">{v.question_text}</div>
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 mb-2">
                      <label className="block sm:col-span-1">
                        <span className="text-[10px] text-slate-500">Dropdown response</span>
                        <input value={v.dropdown_response ?? ''} onChange={e => setVrField(v.id, 'dropdown_response', e.target.value || null)}
                               className="w-full px-2 py-1 text-xs border border-slate-200 dark:border-slate-700 rounded bg-white dark:bg-slate-900 text-slate-900 dark:text-white" />
                      </label>
                      <label className="block sm:col-span-2">
                        <span className="text-[10px] text-slate-500">Remark</span>
                        <input value={v.remark ?? ''} onChange={e => setVrField(v.id, 'remark', e.target.value || null)}
                               className="w-full px-2 py-1 text-xs border border-slate-200 dark:border-slate-700 rounded bg-white dark:bg-slate-900 text-slate-900 dark:text-white" />
                      </label>
                    </div>
                    <label className="block">
                      <span className="text-[10px] text-slate-500">Detailed response</span>
                      <textarea
                        value={v.detailed_response ?? ''}
                        onChange={e => setVrField(v.id, 'detailed_response', e.target.value || null)}
                        rows={3}
                        className="w-full px-2 py-1 text-xs border border-slate-200 dark:border-slate-700 rounded bg-white dark:bg-slate-900 text-slate-900 dark:text-white"
                      />
                    </label>
                  </div>
                ))}
              </div>
            </section>
          )}
        </div>

        <div className="flex items-center justify-end gap-2 px-5 py-3 border-t border-slate-200 dark:border-slate-700">
          <button onClick={onClose} disabled={saving}
                  className="px-3 py-1.5 text-xs border border-slate-200 dark:border-slate-700 rounded hover:bg-slate-50 dark:hover:bg-slate-700">
            Cancel
          </button>
          <button onClick={handleSave} disabled={saving}
                  className="px-3 py-1.5 text-xs bg-slate-900 dark:bg-white text-white dark:text-slate-900 rounded hover:opacity-90 flex items-center gap-1.5">
            <Save className="h-3 w-3" />
            {saving ? 'Saving...' : 'Save all'}
          </button>
        </div>
      </div>
    </div>
  )
}
